import React, { Dispatch, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import DialogFrame from '../_parts/DialogFrame';
import DialogHeader from '../_parts/DialogHeader';
import {
    ISupportTemplate,
    closeProjectSupportTicketCreationDialog,
    getSupportTemplates,
    getUserData,
} from '../../../../redux/project/sliceProjectSupportTicketCreation';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
    clearProjectDocumentsInfo,
    getProjectDocumentsInfo,
    removeProjectDocumentsInfoItem,
} from '../../../../redux/project/documents/sliceProjectDocuments';
import { DialogContent } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';
import Documents from '../_parts/Documents';
import CreateSupportTicketContent from './_parts/CreateSupportTicketContent';
import CreateSupportTicketlDialogActions from './_parts/CreateSupportTicketlDialogActions';
import { updateSelectedDocuments } from '../../../../redux/project/documents/sliceProjectSelectedDocuments';
import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../Toast/Toast';
import {
    clearProjectUserList,
    getUserEmailList,
} from '../../../../redux/project/sliceProjectUserList';
import { resetFileState as resetSupportTicketFiles } from '../../../../redux/import/sliceImportFileUpload';

const initialMainState: ICreateSupportTicketMainState = {
    templateUNID: '',
    templateDescription: '',
    body: '',
    subject: '',
    sendCopyTo: 'true',
    priority: '',
    copyTo: [],
    sendTo: [],
};

const initialErrorState: { [P in keyof ICreateSupportTicketMainState]?: boolean } = {
    templateUNID: false,
    body: false,
    exceededFileSize: false,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'createSupportTicketDialog' });

    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();
    const { replicaId, areaName } = useParams();

    const selectedDocuments = useSelector(
        (store: IAppReduxStore) => store.projectSelectedDocuments.storage,
    );
    const {
        isDialogOpen,
        isSupportTicketCreated,
        loading: loadingCreateSupportTicketData,
    } = useSelector((_store: IAppReduxStore) => _store.projectSupportTicketCreation);
    const { data: documents, loading: loadingInfoDocs } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );
    const loadingEmailOptions = useSelector(
        (_store: IAppReduxStore) => _store.projectUserList.loading,
    );

    const [mainState, setMainState] = useState<ICreateSupportTicketMainState>(initialMainState);
    const [showErrorState, setShowErrorState] = useState(initialErrorState);

    useEffect(() => {
        if (isSupportTicketCreated) {
            dispatch(
                updateSelectedDocuments({
                    replicaId,
                    areaName,
                    documentIds: [],
                }),
            );
            dispatch(clearProjectDocumentsInfo());
            dispatch(clearProjectUserList());

            onClose();
            enqueueSnackbar(t('toastSuccess'), {
                severity: ToastSeverity.Success,
            });
        }
    }, [isSupportTicketCreated]);

    useEffect(() => {
        if (!isDialogOpen) return;

        if (selectedDocuments?.documentIds?.length === 0) {
            dispatch(clearProjectDocumentsInfo());
        }

        dispatch(getSupportTemplates({ replicaId }));
        dispatch(
            getUserData({
                replicaId,
            }),
        );
        selectedDocuments?.documentIds?.length !== 0 &&
            dispatch(
                getProjectDocumentsInfo({
                    replicaId,
                    documentIds: selectedDocuments?.documentIds,
                }),
            );

        dispatch(getUserEmailList({ replicaId: replicaId }));
    }, [selectedDocuments, isDialogOpen]);

    const handleChange = ({ field, value }: { field: string; value: string }) => {
        setShowErrorState({ ...showErrorState, [field]: false });
        setMainState(prevState => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleChangeTemplate = (template: ISupportTemplate) => {
        setMainState(prevState => ({
            ...prevState,
            templateUNID: template.docUNID,
            templateDescription: template.description,
            subject: '',
            body: template.body,
        }));

        setShowErrorState(prevState => ({
            ...prevState,
            templateUNID: false,
            body: false,
        }));
    };

    const resetState = () => {
        setShowErrorState(initialErrorState);
        setMainState({ ...initialMainState });
    };

    const onClose = () => {
        resetState();
        dispatch(resetSupportTicketFiles());
        dispatch(closeProjectSupportTicketCreationDialog());
    };

    const removeDocument = (universalId: string) => {
        dispatch(removeProjectDocumentsInfoItem({ universalId }));
    };

    return (
        <DialogFrame open={isDialogOpen}>
            <DialogHeader onClose={onClose} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={
                        loadingInfoDocs || loadingCreateSupportTicketData || loadingEmailOptions
                    }
                    documentSelection={
                        <Documents documents={documents} removeDocument={removeDocument} />
                    }
                    main={
                        <CreateSupportTicketContent
                            mainState={mainState}
                            handleChange={handleChange}
                            handleChangeTemplate={handleChangeTemplate}
                            showErrorState={showErrorState}
                            setShowErrorState={setShowErrorState}
                        />
                    }
                />
            </DialogContent>
            <CreateSupportTicketlDialogActions
                setShowErrorState={setShowErrorState}
                mainState={mainState}
                onClose={onClose}
            />
        </DialogFrame>
    );
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ICreateSupportTicketMainState {
    templateUNID: string;
    templateDescription: string;
    body: string;
    subject: string;
    sendCopyTo: 'true' | 'false';
    priority: string;
    copyTo: string[];
    sendTo: string[];
    exceededFileSize?: 'true' | 'false';
}
